import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { getIntl, getLocale } from '@umijs/max';
import { message, notification } from 'antd';

// Định nghĩa các loại hiển thị lỗi
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// Cấu trúc dữ liệu phản hồi từ backend
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * @name Cấu hình xử lý lỗi
 * Xử lý lỗi mặc định cho các yêu cầu request (Dựa trên umi request)
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  errorConfig: {
    // Ném lỗi nghiệp vụ
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error;
      }
    },
    // Nhận và xử lý lỗi
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      const intl = getIntl(getLocale());

      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect if needed
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Lỗi từ Axios (Server phản hồi lỗi)
        message.error(`${intl.formatMessage({ id: 'request.error.responseStatus' })}${error.response.status}`);
      } else if (error.request) {
        // Yêu cầu đã gửi nhưng không nhận được phản hồi
        message.error(intl.formatMessage({ id: 'request.error.noResponse' }));
      } else {
        // Lỗi khi thiết lập yêu cầu
        message.error(intl.formatMessage({ id: 'request.error.setupError' }));
      }
    },
  },

  // Bộ chặn yêu cầu (Request Interceptors)
  requestInterceptors: [
    (config: RequestOptions) => {
      return { ...config };
    },
  ],

  // Bộ chặn phản hồi (Response Interceptors)
  responseInterceptors: [
    (response) => {
      const { data } = response as unknown as ResponseStructure;

      if (data?.success === false) {
        const intl = getIntl(getLocale());
        message.error(intl.formatMessage({ id: 'request.error.dataFailed' }));
      }
      return response;
    },
  ],
};
